import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  filter,
  catchError,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs';
import { PortalRoute, PortalRoutes, RouteItem } from '../../../routes/app.types';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { PermissionsMap, PermissionValue, ResourceValue } from '../../../services/permissions/permissions.types';
import { ReportsApiService } from '../../../pages/reporting/services/reports-api.service';
import { ReportBuilderLink } from '../../../pages/reporting/reports.types';
import { TranslateRegistry } from '../../../services/translate/translate.registry';
import { TitleTranslateResolver } from '../../../services/translate/title-translate.resolver';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ToggleApiService } from '../../../services/toggle/toggle-api.service';
import { selectHeadendBaseUrl } from '../../../pages/help-and-settings/user-settings/store/user-settings.reducer';
import { SideMenuStateActions } from './store/side-menu-state.actions';
import { selectIsMenuStateLoaded, selectMenuState } from './store/side-menu-state.reducer';
import { MenuState } from './side-menu.types';

const SORT_BEFORE = -1;
const BUILDER_LINK_EMISSIONS = 2;
export const MENU_STATE_KEY = 'application_menu_state';

const DEFAULT_COLLAPSED_SECTIONS = new Set([
  TranslateRegistry.get.MAIN_MENU.SECTION.ADMINISTRATION,
  TranslateRegistry.get.MAIN_MENU.SECTION.REPORTING
]);

@Injectable()
export class SideMenuService {
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionsService);
  private readonly customReportsService = inject(ReportsApiService);
  readonly #authService = inject(OidcSecurityService);
  private readonly toggleApiService = inject(ToggleApiService);
  private readonly store = inject(Store);

  private readonly userPermissions$: Observable<PermissionsMap> = this.permissionService.permissions$;

  // The first NavigationEnd event is supposed to kick off menu filtering; now that menu creation
  // involves an API call, we have to buffer at least one such event in case we subscribe late.
  private readonly bufferedNavEndEvents$ = new ReplaySubject<NavigationEnd>(1);

  // Gets the Reporting routes (which can't be defined statically) and combines them with the
  // configured routes.
  // shareReplay is used so that we only retrieve the Reporting routes once. This is piped to
  // switchMap to just return the emissions from the combineLatest observable.
  private readonly routes$: Observable<PortalRoutes>;

  private reportBuildingAvailable: boolean = false;

  readonly #menuStateSignal = this.store.selectSignal(selectMenuState);

  public readonly filteredMenuItems$: Observable<readonly RouteItem[]> = this.#authService.isAuthenticated$.pipe(
    filter(({ isAuthenticated }) => isAuthenticated),
    switchMap(() =>
      this.store.select(selectIsMenuStateLoaded).pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          this.routes$.pipe(
            switchMap((routes: PortalRoutes) => {
              // changing routes list in pure function means Angular isn't aware of the updated list
              this.router.resetConfig(routes);

              // Whenever a NavigationEnd event, permissions change, or headendBaseUrl changes, update the menu.
              return combineLatest([
                this.bufferedNavEndEvents$,
                this.userPermissions$,
                this.store.select(selectHeadendBaseUrl).pipe(startWith(null as string | null))
              ]).pipe(
                map(([_, permissions, headendBaseUrl]) => this.createMenuItems(routes, permissions, headendBaseUrl)),
                switchMap((menuItems: RouteItem[]) => this.filterByFeatureToggles(menuItems)),
                map((menuItems: RouteItem[]) => this.groupByCategory(menuItems)),
                map(items => {
                  this.#applyMenuState(items);
                  return items;
                })
              );
            })
          )
        )
      )
    ),
    shareReplay(1)
  );

  constructor() {
    // 'Saves' the last NavigationEnd event.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(event => {
      this.bufferedNavEndEvents$.next(event);
    });

    // Don't build the observables until permissions are set.
    this.routes$ = this.userPermissions$.pipe(
      tap((perms: PermissionsMap) => {
        this.reportBuildingAvailable = this.extractPermission(
          perms,
          ResourceValue.REPORT_BUILDER,
          PermissionValue.VIEW
        );
      }),
      switchMap(() => {
        return this.updatedRoutes();
      }),
      shareReplay(1)
    );
  }

  saveMenuState(items: readonly RouteItem[]): void {
    this.store.dispatch(SideMenuStateActions.saveMenuState({ menuState: this.#extractMenuState(items) }));
  }

  #applyMenuState(items: readonly RouteItem[]): void {
    const state = this.#menuStateSignal();
    let currentSectionExpanded = true;

    for (const item of items) {
      if (item.isSection) {
        currentSectionExpanded = state?.sections[item.title] ?? !DEFAULT_COLLAPSED_SECTIONS.has(item.title);
        item.expanded = currentSectionExpanded;
        continue;
      }
      item.hidden = !currentSectionExpanded;
      this.#applyItemState(item, state, 1);
    }
  }

  #applyItemState(item: RouteItem, state: MenuState | null, level: number): void {
    if (item.children.length > 0 && item.path) {
      item.expanded = state?.items[item.path] ?? false;
    }
    item.children.forEach(child => this.#applyItemState(child, state, level + 1));
  }

  #extractMenuState(items: readonly RouteItem[]): MenuState {
    const sections: Record<string, boolean> = {};
    const itemsState: Record<string, boolean> = {};

    for (const item of items) {
      if (item.isSection) {
        sections[item.title] = item.expanded;
      } else if (item.children.length > 0 && item.path) {
        itemsState[item.path] = item.expanded;
      }
    }

    return { sections, items: itemsState, horizontalExpanded: this.#menuStateSignal()?.horizontalExpanded ?? null };
  }

  private addRoute(routes: PortalRoute[], newRoute: PortalRoute): PortalRoute[] {
    const newRoutes = [...routes];
    newRoutes.splice(routes.length - 1, 0, newRoute);
    return newRoutes;
  }

  private convertLinkToRoute(linkObj: ReportBuilderLink): PortalRoute {
    const loginAddition: string = '/login/keycloak_' + linkObj.clientId + '?';

    const path: string = linkObj.url + loginAddition + 'client_id=' + linkObj.clientId + '&realm=' + linkObj.tenantId;

    const builderRoute: PortalRoute = {
      path: path,
      // This next line is required by Angular's router - every route must have a redirect path or a component.
      // We're using this route object to represent the menu item linking to an external site, since
      // we create the menu items using router.config.
      // In the menu, we just check for the 'hasExternalLink' flag, and if present, we ignore the component
      // or redirectTo argument.
      redirectTo: 'dummy',
      title: TitleTranslateResolver,
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.REPORT_BUILDER,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.REPORT_BUILDER,
          path: path,
          icon: 'table-add',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.REPORTING,
          sort: 0,
          hasExternalLink: true,
          hasRight: {
            resourceValue: ResourceValue.REPORT_BUILDER,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      }
    };

    return builderRoute;
  }

  private createMenuItems(
    routes: PortalRoutes,
    permissions: PermissionsMap,
    headendBaseUrl?: string | null
  ): RouteItem[] {
    const menuItems: RouteItem[] = [];
    for (const route of routes) {
      if (!route.data) continue;
      const { routeItem } = route.data;
      if (routeItem instanceof RouteItem) {
        if (!routeItem.path) continue;
        if (routeItem.hasRight?.resourceValue === ResourceValue.ZONE_SCAN) {
          if (!headendBaseUrl) continue;
          routeItem.path = `${headendBaseUrl}administration/zoneScanGlobalSettings`;
        }
        let hasRight = true;
        routeItem.children = route.children?.length
          ? this.createMenuItems(route.children, permissions, headendBaseUrl)
          : [];
        routeItem.hasSelectedChild = !!routeItem.children.length && this.hasSelectedChild(routeItem.children);
        routeItem.expanded = routeItem.expanded || routeItem.hasSelectedChild;
        routeItem.selected = this.isLinkActive(routeItem);
        if (routeItem.hasRight) {
          const { resourceValue, targetPermissionValue } = routeItem.hasRight;
          hasRight = this.permissionService.hasRightMapper(resourceValue, targetPermissionValue, permissions);
        }
        if (hasRight) {
          menuItems.push(routeItem);
        }
      }
    }
    return menuItems;
  }

  private filterByFeatureToggles(menuItems: RouteItem[]): Observable<RouteItem[]> {
    const allToggledItems = this.collectAllToggledItems(menuItems);

    if (!allToggledItems.length) {
      return of(menuItems.slice());
    }

    return combineLatest(
      allToggledItems.map(item =>
        this.toggleApiService.isEnabled(item.featureToggleKey as string).pipe(
          map(featureToggleDto => ({ item, enabled: featureToggleDto.enabled })),
          catchError(() => of({ item, enabled: false }))
        )
      )
    ).pipe(
      map(results => {
        const disabledItems = new Set(results.filter(result => !result.enabled).map(result => result.item));
        return this.filterMenuItemsRecursively(menuItems, disabledItems);
      })
    );
  }

  private collectAllToggledItems(items: RouteItem[]): RouteItem[] {
    const result: RouteItem[] = [];
    for (const item of items) {
      if (item.featureToggleKey) {
        result.push(item);
      }
      if (item.children.length) {
        result.push(...this.collectAllToggledItems(Array.from(item.children)));
      }
    }
    return result;
  }

  private filterMenuItemsRecursively(items: RouteItem[], disabledItems: Set<RouteItem>): RouteItem[] {
    return items
      .filter(item => !disabledItems.has(item))
      .map(item => {
        if (item.children.length) {
          item.children = this.filterMenuItemsRecursively(Array.from(item.children), disabledItems);
        }
        return item;
      });
  }

  private extractPermission(permissions: PermissionsMap, resource: string, target: string): boolean {
    return permissions.has(resource)
      ? Boolean(
          permissions.get(resource)?.permissions.find(({ permissionValue }) => {
            return target === permissionValue;
          })
        )
      : false;
  }

  private groupByCategory(menuItems: readonly RouteItem[]): readonly RouteItem[] {
    const categorizedMenuItems = menuItems.reduce((acc: Record<string, RouteItem[]>, currentGroup: RouteItem) => {
      const groupId = currentGroup.group!;

      if (acc[groupId]) {
        acc[groupId].push(currentGroup);
      } else {
        acc[groupId] = [currentGroup];
      }

      return acc;
    }, {});

    return Object.entries(categorizedMenuItems).reduce((acc: RouteItem[], [category, children]) => {
      // Sort the children array by the 'sort' attribute, maintaining the original order in case of a tie
      const sortedChildren = children.sort((a, b) => {
        if (a.sort === null && b.sort === null) return 0; // Both are null, keep original order
        if (a.sort === null) return 1; // a is null, b is not, a goes after b
        if (b.sort === null) return SORT_BEFORE; // b is null, a is not, a goes before b
        return a.sort! - b.sort!; // Both are numbers, sort numerically
      });

      return category
        ? acc.concat([
            new RouteItem({
              title: category,
              isSection: true,
              expanded: true
            }),
            ...sortedChildren
          ])
        : [...sortedChildren];
    }, []);
  }

  private isLinkActive(item: RouteItem): boolean {
    if (item.children.length && this.hasSelectedChild(item.children)) {
      return !item.expanded;
    }

    return this.router.isActive(this.router.createUrlTree([`/${item.path}`], { relativeTo: this.route }), {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  private hasSelectedChild(items: readonly RouteItem[]): boolean {
    for (const item of items) {
      const children = item.children;
      if (children.length) {
        return this.hasSelectedChild(children);
      }
      if (item.selected) {
        return item.selected;
      }
    }

    return false;
  }

  private updatedRoutes(): Observable<PortalRoutes> {
    const noBuilderLink: ReportBuilderLink = { url: '', clientId: 0, tenantId: '' };

    const builder$: Observable<ReportBuilderLink> = this.reportBuildingAvailable
      ? this.customReportsService.getReportBuilderLink()
      : of(noBuilderLink);

    return builder$.pipe(
      startWith(noBuilderLink),
      take(BUILDER_LINK_EMISSIONS),
      map(linkObj => {
        let routes: PortalRoutes = this.router.config as PortalRoutes;

        if (linkObj.url.length > 0) {
          const builderRoute = this.convertLinkToRoute(linkObj);
          routes = this.addRoute(routes, builderRoute);
        }

        return routes;
      }),
      // only add the report routes once
      shareReplay(1)
    );
  }
}
